/**
 * useMobileAnalyticsFilters Hook
 * 
 * Manages all filter state and logic for mobile analytics:
 * - Period selection and navigation
 * - Date range management
 * - Account and user filtering
 * - Filter state tracking
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { Account } from "../../../types";
import { WorkspaceMember } from "../../Analytics/hooks/useAnalyticsWorkspaceMembers";

export interface UseMobileAnalyticsFiltersParams {
  workspaceId: string;
  workspaceCreatedAt: Date | null;
  accounts: Account[];
  workspaceMembers: WorkspaceMember[];
  setDateRangeByPeriod: (periodType: string, offset: number, setFromDate: (date: string) => void, setToDate: (date: string) => void) => void;
}

export interface UseMobileAnalyticsFiltersReturn {
  // Date state
  fromDate: string;
  toDate: string;
  setFromDate: (date: string) => void;
  setToDate: (date: string) => void;
  
  // Period state
  selectedPeriod: string;
  currentPeriodOffset: number;
  handlePeriodChange: (period: string) => void;
  handlePeriodNavigation: (direction: "left" | "right") => void;
  
  // Filter state
  selectedAccounts: string[];
  setSelectedAccounts: (accounts: string[]) => void;
  selectedUsers: string[];
  setSelectedUsers: (users: string[]) => void;
  
  // UI state
  filtersExpanded: boolean;
  setFiltersExpanded: (expanded: boolean) => void;
  focusedDateField: "startDate" | "endDate" | null;
  setFocusedDateField: (field: "startDate" | "endDate" | null) => void;
  
  // Filter utilities
  hasActiveFilters: boolean;
  clearAllFilters: () => void;
}

export function useMobileAnalyticsFilters({
  workspaceId,
  workspaceCreatedAt,
  accounts,
  workspaceMembers,
  setDateRangeByPeriod,
}: UseMobileAnalyticsFiltersParams): UseMobileAnalyticsFiltersReturn {
  
  // Initialize date state with default current month values immediately
  // This prevents the visual change when dates are set in useEffect
  const getDefaultDates = () => {
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
    const toStr = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
    return { fromStr, toStr };
  };
  
  const defaultDates = getDefaultDates();
  const [fromDate, setFromDate] = useState<string>(defaultDates.fromStr);
  const [toDate, setToDate] = useState<string>(defaultDates.toStr);
  
  // Period state
  const [selectedPeriod, setSelectedPeriod] = useState<string>("Monthly");
  const [currentPeriodOffset, setCurrentPeriodOffset] = useState<number>(0);
  
  // Filter state
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  
  // UI state
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);
  const [focusedDateField, setFocusedDateField] = useState<"startDate" | "endDate" | null>(null);

  // Set default Monthly period on mount (only if dates are somehow empty)
  // This is a safety check, but dates should already be initialized above
  useEffect(() => {
    if (fromDate && toDate) return;
    
    const { fromStr, toStr } = getDefaultDates();
    setFromDate(fromStr);
    setToDate(toStr);
  }, [workspaceId]);

  // Update date range when workspace creation date loads and "All time" is selected
  useEffect(() => {
    if (selectedPeriod === "All time" && workspaceCreatedAt) {
      setDateRangeByPeriod("All time", 0, setFromDate, setToDate);
    }
  }, [workspaceCreatedAt, selectedPeriod, setDateRangeByPeriod]);

  // Period change handler
  const handlePeriodChange = useCallback((period: string): void => {
    setSelectedPeriod(period);
    setCurrentPeriodOffset(0);
    if (period === "Custom") return;
    if (period === "All time") {
      setDateRangeByPeriod(period, 0, setFromDate, setToDate);
      return;
    }
    setDateRangeByPeriod(period, 0, setFromDate, setToDate);
  }, [setDateRangeByPeriod]);

  // Period navigation handler
  const handlePeriodNavigation = useCallback((direction: "left" | "right"): void => {
    if (!selectedPeriod || selectedPeriod === "All time" || selectedPeriod === "Custom") return;
    
    const newOffset = direction === "left" 
      ? currentPeriodOffset - 1 
      : currentPeriodOffset + 1;
    
    setCurrentPeriodOffset(newOffset);
    setDateRangeByPeriod(selectedPeriod, newOffset, setFromDate, setToDate);
  }, [selectedPeriod, currentPeriodOffset, setDateRangeByPeriod]);

  // Clear all filters handler
  const clearAllFilters = useCallback(() => {
    setSelectedAccounts([]);
    setSelectedUsers([]);
    setSelectedPeriod("Monthly");
    setCurrentPeriodOffset(0);
    
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
    const toStr = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
    setFromDate(fromStr);
    setToDate(toStr);
  }, []);

  // Check if there are active filters
  const hasActiveFilters = useMemo(() => {
    const periodIsDefault = selectedPeriod === "Monthly" && currentPeriodOffset === 0;
    
    const today = new Date();
    const defaultFrom = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultTo = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const defaultFromStr = `${defaultFrom.getFullYear()}-${String(defaultFrom.getMonth() + 1).padStart(2, '0')}-${String(defaultFrom.getDate()).padStart(2, '0')}`;
    const defaultToStr = `${defaultTo.getFullYear()}-${String(defaultTo.getMonth() + 1).padStart(2, '0')}-${String(defaultTo.getDate()).padStart(2, '0')}`;
    const datesMatchDefault = fromDate === defaultFromStr && toDate === defaultToStr;
    const datesAreActive = !datesMatchDefault || currentPeriodOffset !== 0 || selectedPeriod !== "Monthly";
    
    const allAccountsSelected = accounts.length > 0 && 
      (selectedAccounts.length === 0 || selectedAccounts.length === accounts.length);
    const userFilterActive = selectedUsers.length > 0 && selectedUsers.length < workspaceMembers.length;
    
    return (
      !periodIsDefault ||
      datesAreActive ||
      (accounts.length > 0 && !allAccountsSelected) ||
      userFilterActive
    );
  }, [fromDate, toDate, selectedPeriod, currentPeriodOffset, selectedAccounts.length, accounts.length, selectedUsers.length, workspaceMembers.length]);

  return {
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    selectedPeriod,
    currentPeriodOffset,
    handlePeriodChange,
    handlePeriodNavigation,
    selectedAccounts,
    setSelectedAccounts,
    selectedUsers,
    setSelectedUsers,
    filtersExpanded,
    setFiltersExpanded,
    focusedDateField,
    setFocusedDateField,
    hasActiveFilters,
    clearAllFilters,
  };
}
